/**
 * A origem do projeto na tela, pela carga do ex-kit (#1785).
 *
 * Quem lê a origem é o ex-kit; o que o projeto tem dela é a carga. O cartão da Visão geral e a
 * página Conexões mostram a vigente (a última fechada) e avisam da que está chegando. Projeto sem
 * carga vê o aviso que antes só aparecia ao rodar o Functional ("projeto sem carga do ex-kit").
 */

import type { TFunction } from "i18next";

import { formatDate, formatDateOnly } from "@/lib/datetime";
import { formatCount } from "@/lib/format";
import { tp } from "@/lib/plural";
import { PROJECT_MODE_LABEL, SOURCE_TYPE_LABEL, type SourceType } from "@/lib/projectSource";
import type { PillTone } from "@/components/brand/Pill";
import type { Project, ProjectCurrentLoad, ProjectLoadSummary } from "@/types/api";

export type SourceLoadState = "loading" | "current" | "receiving" | "none" | "unavailable";

export function sourceLoadState(
  data: ProjectCurrentLoad | undefined,
  isLoading: boolean,
  isError: boolean,
): SourceLoadState {
  if (isError) return "unavailable";
  if (isLoading || !data) return "loading";
  if (data.current) return "current";
  if (data.receiving) return "receiving";
  return "none";
}

/** A carga que a tela descreve: a vigente; sem ela, a que está chegando. */
export function shownLoad(data: ProjectCurrentLoad | undefined): ProjectLoadSummary | null {
  return data?.current ?? data?.receiving ?? null;
}

/** Sigla da origem no número grande do cartão, ao lado de "→ S/4HANA". */
export const SOURCE_TYPE_SHORT: Record<SourceType, string> = {
  ecc: "ECC",
  s4hana: "S/4",
  business_one: "B1",
  other: "ERP",
};

/** Chaves de i18n do extrator (`source.load.extractor_kind`); o texto em inglês é a chave. */
const EXTRACTOR_KIND_LABEL: Record<string, string> = {
  abap_package: "ABAP package",
  rfc_client: "RFC client",
  sql: "SQL",
  file: "File",
};

export function statePill(t: TFunction, state: SourceLoadState): { tone: PillTone; label: string } {
  switch (state) {
    case "current":
      return { tone: "ok", label: t("Current load") };
    case "receiving":
      return { tone: "gold", label: t("Receiving") };
    case "none":
      return { tone: "gold", label: t("No load") };
    case "unavailable":
      return { tone: "danger", label: t("Unavailable") };
    default:
      return { tone: "muted", label: "…" };
  }
}

/** A frase de baixo: o que falta, ou a carga nova que está chegando. `null` quando não há. */
export function stateNote(
  t: TFunction,
  state: SourceLoadState,
  data: ProjectCurrentLoad | undefined,
): string | null {
  if (state === "none") {
    return t(
      "No extraction kit load yet. The agents stop at the first step until the first load arrives.",
    );
  }
  if (state === "unavailable") {
    return t("Could not read the client database. Try again in a few minutes.");
  }
  const receiving = data?.receiving;
  if (!receiving) return null;
  return state === "receiving"
    ? tp(
        t,
        receiving.table_count,
        "The first load is arriving: 1 table so far.",
        "The first load is arriving: {{n}} tables so far.",
      )
    : tp(
        t,
        receiving.table_count,
        "A new load is arriving: 1 table so far.",
        "A new load is arriving: {{n}} tables so far.",
      );
}

export function sourceLabel(t: TFunction, project: Pick<Project, "source_type"> | undefined) {
  return project?.source_type ? t(SOURCE_TYPE_LABEL[project.source_type]) : "—";
}

export function modeLabel(t: TFunction, project: Pick<Project, "mode"> | undefined) {
  return project?.mode ? t(PROJECT_MODE_LABEL[project.mode]) : "—";
}

/** As linhas do painel da página Conexões; o cartão da Visão geral usa as quatro primeiras. */
export function loadRows(
  t: TFunction,
  project: Pick<Project, "mode"> | undefined,
  load: ProjectLoadSummary | null,
): { k: string; v: string; mono?: boolean }[] {
  const extractor = load
    ? `${t(EXTRACTOR_KIND_LABEL[load.extractor_kind] ?? load.extractor_kind)} ${load.extractor_version}`
    : "—";
  return [
    { k: t("Mode"), v: modeLabel(t, project) },
    { k: t("Source snapshot"), v: load ? formatDate(load.snapshot_at) : "—" },
    { k: t("Tables"), v: load ? formatCount(load.table_count) : "—", mono: true },
    { k: t("Rows"), v: load ? formatCount(load.row_count) : "—", mono: true },
    {
      k: t("Window"),
      v: load ? `${formatDateOnly(load.window_start)} → ${formatDateOnly(load.window_end)}` : "—",
    },
    { k: t("Extractor"), v: extractor },
  ];
}
